import * as stylex from '@stylexjs/stylex'
import { useState } from 'react'

import Avatar from './Avatar'
import Dialog from './Dialog'
import Icon from './Icon'
import type { CreateGroupInput, Overview } from '../lib/ledger'


const styles = stylex.create({
  s0: {
    display: "flex",
    flexDirection: "column",
    rowGap: "24px",
  },
  s1: {
    display: "block",
  },
  s2: {
    marginBottom: "8px",
    display: "block",
    fontSize: "13px",
    fontWeight: 600,
    color: "#3d463f",
  },
  s3: {
    height: "48px",
    width: "100%",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#fff",
    paddingInline: "14px",
    fontSize: "14px",
    outline: "none",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    "::placeholder": {
      color: "#99a099",
    },
    ":focus": {
      borderColor: "#5b9a6c",
      boxShadow: "0 0 0 1px color-mix(in srgb, #5b9a6c 10%, transparent)",
    },
  },
  s4: {
    fontWeight: 400,
    color: "#869087",
  },
  s5: {
    marginBottom: "8px",
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
  },
  s6: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#3d463f",
  },
  s7: {
    fontSize: "11px",
    color: "#758078",
  },
  s8: {
    overflow: "hidden",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#e0e6de",
    backgroundColor: "#fff",
  },
  s9: {
    display: "flex",
    cursor: "pointer",
    alignItems: "center",
    gap: "12px",
    borderBottomWidth: 1,
    borderColor: "#edf0ec",
    paddingInline: "12px",
    paddingBlock: "12px",
    ":last-child": {
      borderBottomColor: "0",
    },
  },
  s10: {
    height: "16px",
    width: "16px",
    borderRadius: "0.25rem",
    borderColor: "#bdc9bc",
    accentColor: "#4e9360",
  },
  s11: {
    flex: "1 1 0%",
    fontSize: "13px",
    fontWeight: 500,
  },
  s12: {
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#f0d0c0",
    backgroundColor: "#fff5f0",
    paddingInline: "14px",
    paddingBlock: "12px",
    fontSize: "13px",
    color: "#9b491e",
  },
  s13: {
    display: "flex",
    flexDirection: "column-reverse",
    gap: "12px",
    borderTopWidth: 1,
    borderColor: "#e8ece7",
    paddingTop: "20px",
    "@media (min-width: 640px)": {
      flexDirection: "row",
      justifyContent: "end",
    },
  },
  s14: {
    height: "44px",
    borderRadius: "0.75rem",
    paddingInline: "16px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#607068",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#f0f4ef",
    },
  },
  s15: {
    display: "flex",
    height: "44px",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: "0.75rem",
    backgroundColor: "#408b56",
    paddingInline: "20px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#fff",
    boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#347c48",
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.6,
    },
  },
  s16: {
    height: "16px",
    width: "16px",
  },
})
type CreateGroupDialogProps = {
  data: Overview
  onClose: () => void
  onSave: (input: CreateGroupInput) => Promise<void>
}

export default function CreateGroupDialog({ data, onClose, onSave }: CreateGroupDialogProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [memberIDs, setMemberIDs] = useState<string[]>([])
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  function toggleMember(id: string) {
    setMemberIDs((current) => current.includes(id) ? current.filter((personID) => personID !== id) : [...current, id])
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!name.trim()) {
      setError('Give the group a clear name.')
      return
    }
    setSaving(true)
    setError('')
    try {
      await onSave({ name: name.trim(), description: description.trim(), memberIds: memberIDs })
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'We could not create that group.')
      setSaving(false)
    }
  }

  return (
    <Dialog description="Start with the people and moments that belong together." onClose={saving ? () => undefined : onClose} title="Create a group">
      <form {...stylex.props(styles.s0)} onSubmit={submit}>
        <label {...stylex.props(styles.s1)}><span {...stylex.props(styles.s2)}>Group name</span><input autoFocus {...stylex.props(styles.s3)} data-dialog-autofocus maxLength={80} onChange={(event) => setName(event.target.value)} placeholder="Weekend trip, home, dinner club…" value={name} /></label>
        <label {...stylex.props(styles.s1)}><span {...stylex.props(styles.s2)}>A short note <span {...stylex.props(styles.s4)}>(optional)</span></span><input {...stylex.props(styles.s3)} maxLength={180} onChange={(event) => setDescription(event.target.value)} placeholder="What will you share here?" value={description} /></label>
        <fieldset><div {...stylex.props(styles.s5)}><legend {...stylex.props(styles.s6)}>Add people</legend><span {...stylex.props(styles.s7)}>You are included</span></div><div {...stylex.props(styles.s8)}>{data.people.map((person) => <label {...stylex.props(styles.s9)} key={person.id}><input checked={memberIDs.includes(person.id)} {...stylex.props(styles.s10)} onChange={() => toggleMember(person.id)} type="checkbox" /><Avatar person={person} size={32} /><span {...stylex.props(styles.s11)}>{person.name}</span></label>)}</div></fieldset>
        {error && <p {...stylex.props(styles.s12)} role="alert">{error}</p>}
        <div {...stylex.props(styles.s13)}><button {...stylex.props(styles.s14)} disabled={saving} onClick={onClose} type="button">Cancel</button><button {...stylex.props(styles.s15)} disabled={!name.trim() || saving} type="submit">{saving ? 'Creating…' : 'Create group'}<Icon {...stylex.props(styles.s16)} name="arrow-right" /></button></div>
      </form>
    </Dialog>
  )
}
